import { createContactApi } from "@/api/services/main/contact";
import { useContactTypeList } from "@/api/services/main/contact-type";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { CONTACT_TYPE_DEFAULT } from "@/features/contact/constants/contact";
import { ConfirmReplaceContent } from "@/features/contact/components/ConfirmReplaceContent";
import { UploadContactFileBtn } from "@/features/contact/components/UploadContactFileBtn";
import { ContactSchema } from "@/features/contact/schema/contactSchema";
import { TContactForm } from "@/features/contact/types/contactForm";
import { useDialog } from "@/hooks/useDialog";
import { AppRoutes } from "@/routes/config";
import { black } from "@/theme/colors";
import { FCC } from "@/types/common";
import { setErrorForm, showError } from "@/utils/error";
import { isEmpty } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { UNSAVED_CHANGES_MESSAGE } from "@/constants/common";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { BlockerFunction, Location, useBlocker, useNavigate } from "react-router-dom";

type TContactFormProps = {
  companyName: string;
  userName: string;
  previousValues: Partial<TContactForm>;
  type: string;
};

const initForm: Partial<TContactForm> = {
  companyId: undefined,
  createdBy: undefined,
  contactTypeId: undefined,
  content: "",
  email: "",
  telNumber: undefined,
  contactFiles: [],
};

const isOnlyQueryParamsChanged = (currentLocation: Location, nextLocation: Location): boolean => {
  return currentLocation.pathname === nextLocation.pathname;
};

const normalizeValue = (val: unknown) => {
  if (val === null || val === undefined || val === "") return null;
  return val;
};

const hasActualChanges = (
  dirtyFields: Partial<Readonly<Record<keyof TContactForm, boolean | boolean[]>>>,
  currentValues: TContactForm,
  defaultValues: Partial<TContactForm>,
): boolean => {
  return Object.keys(dirtyFields).some((key) => {
    const currentValue = currentValues[key as keyof TContactForm];
    const defaultValue = defaultValues[key as keyof TContactForm];
    const normalizedCurrent = normalizeValue(currentValue);
    const normalizedDefault = normalizeValue(defaultValue);
    if (Array.isArray(normalizedCurrent) && Array.isArray(normalizedDefault)) {
      return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedDefault);
    }
    return normalizedCurrent !== normalizedDefault;
  });
};

const buildFormData = (data: TContactForm): FormData => {
  const formData = new FormData();

  if (data.companyId) formData.append("companyId", String(data.companyId));
  if (data.createdBy) formData.append("createdBy", String(data.createdBy));
  if (data.contactTypeId) formData.append("contactTypeId", String(data.contactTypeId));
  if (data.telNumber) formData.append("telNumber", String(data.telNumber));

  formData.append("content", data.content ?? "");
  formData.append("email", data.email ?? "");

  if (Array.isArray(data.contactFiles)) {
    data.contactFiles.forEach((file) => {
      formData.append("contactFiles", file as unknown as File);
    });
  }

  return formData;
};

export const ContactForm: FCC<TContactFormProps> = ({ companyName, userName, previousValues, type }) => {
  const navigate = useNavigate();
  const confirmReplace = useDialog();
  const { data: contactTypeListResult } = useContactTypeList();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState(false);

  const methods = useForm<TContactForm>({
    defaultValues: { ...initForm, ...previousValues },
    resolver: yupResolver(ContactSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const {
    control,
    setValue,
    getValues,
    formState: { dirtyFields, isSubmitted, errors },
  } = methods;
  const contactTypeId = useWatch({ control, name: "contactTypeId" });

  const contactTypeOptions = useMemo(() => {
    return contactTypeListResult?.data.map((contactType) => ({
      label: truncateWithEllipsis(contactType.name, 60),
      value: contactType.id,
    }));
  }, [contactTypeListResult]);

  useEffect(() => {
    if (!contactTypeOptions || !type) return;

    const isValidType = contactTypeOptions.some((option) => String(option.value) === type);

    if (!isValidType) {
      navigate(getDynamicRoute(AppRoutes.contact, {}, `type=${CONTACT_TYPE_DEFAULT}`));
    }
  }, [contactTypeOptions, type, navigate]);

  const handleChangeTemplate = () => {
    if (!contactTypeListResult?.data) return;

    const selectedContactType = contactTypeListResult.data.find((contactType) => String(contactType.id) === contactTypeId);

    if (selectedContactType) {
      setValue("content", selectedContactType.template ?? "");
      confirmReplace.cancel();
    }
  };

  useEffect(() => {
    if (!contactTypeId || !contactTypeListResult?.data) return;

    if (Boolean(dirtyFields.content)) {
      confirmReplace.show();
    } else {
      handleChangeTemplate();
    }
  }, [contactTypeId, contactTypeListResult]);

  const onSubmit = async (data: TContactForm) => {
    try {
      const formData = buildFormData(data);
      const rs = await createContactApi(formData);

      toast.success(rs.message ?? "お問い合わせを送信しました。");
      methods.reset(data);
      setBlockerEnabled(false);
      setRedirect(true);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const shouldBlock: BlockerFunction = useMemo(
    () =>
      ({ currentLocation, nextLocation }) => {
        if (!blockerEnabled || (isSubmitted && isEmpty(errors))) return false;
        if (isEmpty(dirtyFields)) return false;

        const currentValues = getValues();
        const defaultValues = { ...initForm, ...previousValues };

        if (!hasActualChanges(dirtyFields, currentValues, defaultValues)) return false;

        const isPathnameChanged = currentLocation.pathname !== nextLocation.pathname;
        return !isOnlyQueryParamsChanged(currentLocation, nextLocation) && isPathnameChanged;
      },
    [isSubmitted, errors, dirtyFields, getValues, previousValues, blockerEnabled],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(AppRoutes.home);
      setRedirect(false);
    }
  }, [blockerEnabled, redirect, navigate]);

  useEffect(() => {
    if (blocker.state === "blocked" && !blockerEnabled) {
      blocker.proceed();
    }
  }, [blocker, blockerEnabled]);

  useEffect(() => {
    if (!blockerEnabled || isEmpty(dirtyFields)) return;

    const beforeUnLoadHandler = (event: BeforeUnloadEvent) => {
      const currentValues = getValues();
      const defaultValues = { ...initForm, ...previousValues };
      if (hasActualChanges(dirtyFields, currentValues, defaultValues)) {
        event.preventDefault();
        event.returnValue = UNSAVED_CHANGES_MESSAGE;
        return event;
      }
    };

    window.addEventListener("beforeunload", beforeUnLoadHandler);
    return () => window.removeEventListener("beforeunload", beforeUnLoadHandler);
  }, [blockerEnabled, dirtyFields, getValues, previousValues]);

  const handleConfirmLeave = () => {
    setBlockerEnabled(false);
    blocker.state === "blocked" && blocker.proceed();
  };

  const handleCancelLeave = () => {
    blocker.state === "blocked" && blocker.reset();
  };

  return (
    <>
      <Form methods={methods} onSubmit={onSubmit} schema={ContactSchema} enableBlocker={false}>
        <Stack gap={2}>
          <InnerBox padding={4} showDivider={false}>
            <Stack gap={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormField control={control} name="companyId">
                    <Select readonly options={[{ label: companyName, value: String(previousValues.companyId) }]} />
                  </FormField>
                </Grid>
                <Grid item xs={6}></Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="createdBy">
                    <Select readonly options={[{ label: userName, value: String(previousValues.createdBy) }]} />
                  </FormField>
                </Grid>
                <Grid item xs={6}></Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="contactTypeId">
                    <Select options={contactTypeOptions || []} colorPrevious={black[30]} />
                  </FormField>
                </Grid>
                <Grid item xs={6}></Grid>
                <Grid item xs={12}>
                  <FormField control={control} name="content">
                    <Textarea />
                  </FormField>
                </Grid>
                <Grid item xs={4}>
                  <Stack paddingBottom="4px">
                    <Typography variant="cap12">添付ファイル</Typography>
                  </Stack>
                  <FormField control={control} name="contactFiles">
                    <UploadContactFileBtn />
                  </FormField>
                </Grid>
                <Grid item xs={6}></Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="email">
                    <Input colorPrevious={"#0B0C0D"} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="telNumber">
                    <Input />
                  </FormField>
                </Grid>
              </Grid>
            </Stack>
          </InnerBox>
          <InnerBox showDivider={false} sx={{ boxShadow: "0px 5px 10px rgba(0,0,0,0.25)" }}>
            <Stack alignItems="end">
              <Button type="submit" variant="primary">
                送信
              </Button>
            </Stack>
          </InnerBox>
        </Stack>
      </Form>
      <ConfirmReplaceContent open={confirmReplace.open} onClose={confirmReplace.cancel} onOk={handleChangeTemplate} />
      {blocker.state === "blocked" && blockerEnabled && (
        <Modal isOpen onClose={handleCancelLeave} onOk={handleConfirmLeave} labelClose="いいえ" labelOk="はい" width={500}>
          <Stack mb={2}>
            <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
          </Stack>
        </Modal>
      )}
    </>
  );
};
